import { CheckOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { useMemo, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixDayCoverage,
  IWorkJourneyMatrixUser,
} from '@src/models/interfaces/workJourneyManagement.interface';

import { isMatrixCoverOnlyUser } from '../../utils/isMatrixCoverOnlyUser';
import { getMatrixCellVisual, type MatrixCellVisualVariant } from '../../utils/matrixCellDisplay';
import { getMatrixCellTooltipTitle } from '../../utils/matrixCellTooltip';
import { buildMatrixRestDateSet } from '../../utils/matrixRestDays';
import styles from './styles.module.less';

export type MatrixSelectionKey = string;

const selectionKey = (userId: string, date: string): MatrixSelectionKey => `${userId}|${date}`;

const parseSelectionKey = (key: MatrixSelectionKey): { userId: string; date: string } | null => {
  const separatorIndex = key.indexOf('|');
  if (separatorIndex <= 0 || separatorIndex === key.length - 1) {
    return null;
  }
  return {
    userId: key.substring(0, separatorIndex),
    date: key.substring(separatorIndex + 1),
  };
};

const VARIANT_CLASS: Partial<Record<MatrixCellVisualVariant, string>> = {
  present: styles.cellPresent,
  planned: styles.cellPlanned,
  rest: styles.cellRest,
  WORK: styles.cellWork,
  PRE: styles.cellPresent,
  D: styles.cellRest,
  AUJ: styles.cellAuj,
  AUI: styles.cellAui,
  VAC: styles.cellVac,
  LM: styles.cellLm,
  PGR: styles.cellPgr,
  PSG: styles.cellPsg,
  REM: styles.cellRem,
  replacement_out: styles.cellRemOut,
  ABS: styles.cellAbs,
  PREN: styles.cellPren,
  POSTN: styles.cellPostn,
};

interface WorkJourneyMonthMatrixGridProps {
  days: string[];
  users: IWorkJourneyMatrixUser[];
  cells: IWorkJourneyMatrixCell[];
  coverage: IWorkJourneyMatrixDayCoverage[];
  /** workShift.daysOfWeek (ISO 1=Mon…7=Sun) for rest-day chip overrides. */
  daysOfWeek?: Array<string | number> | null;
  selectedKeys: Set<MatrixSelectionKey>;
  onCellMouseDown: (userId: string, date: string, event: MouseEvent) => void;
  onCellMouseEnter: (userId: string, date: string, event: MouseEvent) => void;
  onCellMouseUp: () => void;
  onCellClick: (userId: string, date: string) => void;
}

const weekdayShort = (dateIso: string, locale: string) => {
  const date = new Date(`${dateIso}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { weekday: 'short' })
    .format(date)
    .replace('.', '')
    .slice(0, 2)
    .toUpperCase();
};

const dayNumber = (dateIso: string) => {
  const date = new Date(`${dateIso}T12:00:00`);
  return String(date.getDate()).padStart(2, '0');
};

const todayIsoInChile = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

export const WorkJourneyMonthMatrixGrid = ({
  days,
  users,
  cells,
  coverage,
  daysOfWeek,
  selectedKeys,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onCellClick,
}: WorkJourneyMonthMatrixGridProps) => {
  const intl = useIntl();
  const locale = intl.locale === 'es' ? 'es-CL' : 'en-US';
  const todayIso = useMemo(() => todayIsoInChile(), []);

  const cellMap = useMemo(() => {
    const map = new Map<string, IWorkJourneyMatrixCell>();
    cells.forEach(cell => map.set(selectionKey(cell.userId, cell.date), cell));
    return map;
  }, [cells]);

  const restDates = useMemo(
    () => buildMatrixRestDateSet(days, cells, daysOfWeek),
    [days, cells, daysOfWeek],
  );

  const coverageByDate = useMemo(() => {
    const map = new Map<string, IWorkJourneyMatrixDayCoverage>();
    coverage.forEach(entry => map.set(entry.date, entry));
    return map;
  }, [coverage]);

  const coverOnlyUserIds = useMemo(() => {
    const ids = new Set<string>();
    users.forEach(user => {
      if (isMatrixCoverOnlyUser(user)) {
        ids.add(user.id);
      }
    });
    return ids;
  }, [users]);

  const renderCellContent = (variant: MatrixCellVisualVariant, label: string) => {
    if (variant === 'empty') return null;
    if (variant === 'planned') return <CheckOutlined className={styles.workIcon} />;
    if (variant === 'rest' || label === 'D') return <span className={styles.restLabel}>D</span>;
    return <span className={styles.codeLabel}>{label}</span>;
  };

  return (
    <div
      className={styles.gridScroll}
      onMouseLeave={onCellMouseUp}
      onMouseUp={onCellMouseUp}
      role="presentation"
    >
      <table className={styles.gridTable}>
        <thead>
          <tr>
            <th className={styles.userColHeader}>
              {intl.formatMessage({ id: 'workJourney.management.matrix.columns.user' })}
            </th>
            {days.map(date => {
              const isWeekend = [0, 6].includes(new Date(`${date}T12:00:00`).getDay());
              const isToday = date === todayIso;
              return (
                <th
                  key={date}
                  className={classNames(
                    styles.dayHeader,
                    isWeekend && styles.weekendCol,
                    isToday && styles.todayCol,
                  )}
                >
                  {isToday && (
                    <div className={styles.todayBadge}>
                      {intl.formatMessage({ id: 'workJourney.management.matrix.today' })}
                    </div>
                  )}
                  <div className={classNames(styles.weekday, isWeekend && styles.weekendWeekday)}>
                    {weekdayShort(date, locale)}
                  </div>
                  <div className={styles.dayNum}>{dayNumber(date)}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr key={user.id}>
              <td className={styles.userCell}>
                <div className={styles.userRow}>
                  <MemberAvatar
                    size={32}
                    user={{
                      displayName: user.displayName,
                      photoURL: user.photoURL ?? undefined,
                    }}
                  />
                  <div className={styles.userMeta}>
                    <div className={styles.userName}>{user.displayName}</div>
                    <div className={styles.userHours}>
                      {intl.formatMessage(
                        { id: 'workJourney.management.matrix.programmedHours' },
                        { hours: user.programmedHours },
                      )}
                      {coverOnlyUserIds.has(user.id) && (
                        <Tag className={styles.replacementTag}>
                          {intl.formatMessage({
                            id: 'workJourney.management.matrix.legend.rem',
                          })}
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              {days.map(date => {
                const key = selectionKey(user.id, date);
                const cell = cellMap.get(key);
                const visual = getMatrixCellVisual(cell, { restDates });
                const isWeekend = [0, 6].includes(new Date(`${date}T12:00:00`).getDay());
                const isToday = date === todayIso;
                const selected = selectedKeys.has(key);
                const cellClass = VARIANT_CLASS[visual.variant];
                const tooltipTitle = getMatrixCellTooltipTitle(intl, cell, user, {
                  restDates,
                  date,
                  daysOfWeek,
                  isCoverOnly: coverOnlyUserIds.has(user.id),
                });

                return (
                  <td
                    key={key}
                    className={classNames(
                      styles.dayCell,
                      isWeekend && styles.weekendCol,
                      isToday && styles.todayCol,
                      cellClass,
                      selected && styles.cellSelected,
                    )}
                    onMouseDown={event => onCellMouseDown(user.id, date, event)}
                    onMouseEnter={event => onCellMouseEnter(user.id, date, event)}
                    onMouseUp={onCellMouseUp}
                    onClick={() => onCellClick(user.id, date)}
                  >
                    <Tooltip
                      title={tooltipTitle}
                      mouseEnterDelay={0.35}
                      destroyTooltipOnHide
                      overlayClassName={styles.cellTooltip}
                    >
                      <div className={styles.cellInner}>
                        {renderCellContent(visual.variant, visual.label)}
                      </div>
                    </Tooltip>
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className={styles.coverageRow}>
            <td className={styles.coverageLabel}>
              {intl.formatMessage({ id: 'workJourney.management.matrix.coverage' })}
            </td>
            {days.map(date => {
              const entry = coverageByDate.get(date);
              const working = entry?.working ?? 0;
              const required = entry?.required ?? 0;
              const below = entry?.belowMinimum ?? false;
              const isToday = date === todayIso;
              return (
                <td
                  key={`cov-${date}`}
                  className={classNames(
                    styles.coverageCell,
                    isToday && styles.todayCol,
                    below ? styles.coverageBelow : styles.coverageOk,
                  )}
                >
                  {working}/{required}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export { selectionKey, parseSelectionKey };
